import { BoundingBox, ResizeHandle } from "../types";

/** Percent of the canvas a box may not shrink below on either axis. */
export const MIN_BBOX_SIZE = 5;

export const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

/**
 * Applies a pointer delta (already expressed in % of the canvas) to a box.
 *
 * With no `handle` this is a move: the box keeps its size and is clamped so it stays
 * fully inside 0..100. With a corner handle it's a resize anchored at the opposite
 * corner, never shrinking below `minSize` on either axis.
 *
 * Shared by the object bboxes and the free-drawn embed area so both behave identically
 * under the same gestures.
 */
export const applyDrag = (
	startBbox: BoundingBox,
	dx: number,
	dy: number,
	handle: ResizeHandle,
	minSize: number = MIN_BBOX_SIZE,
): BoundingBox => {
	if (!handle) {
		return {
			left: clamp(startBbox.left + dx, 0, 100 - startBbox.width),
			top: clamp(startBbox.top + dy, 0, 100 - startBbox.height),
			width: startBbox.width,
			height: startBbox.height,
		};
	}

	const { left, top, width, height } = startBbox;
	const right = left + width;
	const bottom = top + height;

	switch (handle) {
		case "nw":
			return {
				left: clamp(left + dx, 0, right - minSize),
				top: clamp(top + dy, 0, bottom - minSize),
				width: right - clamp(left + dx, 0, right - minSize),
				height: bottom - clamp(top + dy, 0, bottom - minSize),
			};
		case "ne":
			return {
				left,
				top: clamp(top + dy, 0, bottom - minSize),
				width: clamp(width + dx, minSize, 100 - left),
				height: bottom - clamp(top + dy, 0, bottom - minSize),
			};
		case "sw":
			return {
				left: clamp(left + dx, 0, right - minSize),
				top,
				width: right - clamp(left + dx, 0, right - minSize),
				height: clamp(height + dy, minSize, 100 - top),
			};
		case "se":
		default:
			return {
				left,
				top,
				width: clamp(width + dx, minSize, 100 - left),
				height: clamp(height + dy, minSize, 100 - top),
			};
	}
};
